import { useState, useEffect, useCallback, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, Link } from 'react-router-dom';
import { Heart, TrendingUp, Clock, X, Copy, Check, LogIn, BookmarkPlus, LayoutGrid, Layers, Shuffle, Flower, Sparkles, Stars, UserPlus, Search, Tag } from 'lucide-react';
import { MOODS, parseMoods } from '../utils/moods';
import { MoodChips, MOOD_COLORS, SELECTED_COLORS } from '../components/MoodPicker';

import PageHelpModal from '../components/PageHelpModal';
import ApplyPublisherModal from '../components/ApplyPublisherModal';
import { useAuth } from '../contexts/AuthContext';
import {
  fetchPopularPalettes,
  fetchRecentPalettes,
  fetchLastSyncedAt,
  checkIfHearted,
  heartPalette,
  isTrustedPublisher,
} from '../services/galleryService';
import { decodePaletteFromShare } from '../utils/paletteCode';
import { useAppContext } from '../contexts/AppContext';
import { useI18n } from '../contexts/I18nContext';
import { savePalette, getUserPalettes } from '../config/savedPalettes';
import { getUserCollection } from '../utils/userCollection';
import { convertMarkersToCollection } from '../utils/colorConvert';
import { getMarkerCode as getMarkerCodeUtil } from '../utils/color';

// Gallery is live for everyone.
const checkGalleryAccess = () => Promise.resolve(true);

// ─── Heart delta localStorage helpers ────────────────────────────────────────
// delta meaning:
//   +1  → user hearted, sync hasn't counted it yet
//   -1  → user un-hearted after sync already counted their heart
//    0  → snapshot is accurate, no pending change
//
// Keys per palette id:
//   heartDelta_<id>   "-1" | "1" (absent = 0)
//   heartBase_<id>    last snapshot count we saw (used to detect a sync run)

function getHeartDelta(id) {
  return parseInt(localStorage.getItem(`heartDelta_${id}`) || '0', 10);
}

function setHeartDelta(id, delta) {
  if (delta === 0) localStorage.removeItem(`heartDelta_${id}`);
  else localStorage.setItem(`heartDelta_${id}`, String(delta));
}

// Called every time we receive a snapshot count for a palette.
// Returns the delta to use AFTER reconciliation.
function reconcileHeartBase(id, snapshotCount) {
  const baseKey = `heartBase_${id}`;
  const lastBase = localStorage.getItem(baseKey);
  const prevBase = lastBase !== null ? parseInt(lastBase, 10) : null;

  localStorage.setItem(baseKey, String(snapshotCount));

  if (prevBase !== null && prevBase !== snapshotCount) {
    // Snapshot changed → sync ran. The snapshot now reflects reality.
    // Clear any pending delta — the server state is authoritative.
    localStorage.removeItem(`heartDelta_${id}`);
    return 0;
  }
  return getHeartDelta(id);
}

function displayHeartCount(palette) {
  const base = palette.heartCount || 0;
  const delta = reconcileHeartBase(palette.id, base);
  return Math.max(0, base + delta);
}

// ─── Last sync label ──────────────────────────────────────────────────────────

function formatLastSync(isoString) {
  if (!isoString) return null;
  const diffMs = Date.now() - new Date(isoString).getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return 'Last sync less than 1 hour ago';
  if (diffHours === 1) return 'Last sync about 1 hour ago';
  return `Last sync about ${diffHours} hours ago`;
}

// ─── Gallery Palette Card ─────────────────────────────────────────────────────

function formatPaletteDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return null;
  }
}

function GalleryCard({ palette, onClick }) {
  const colors = palette.previewColors || [];
  const moodNames = parseMoods(palette.moods);

  return (
    <button
      onClick={() => onClick(palette)}
      className="group w-full bg-white rounded-2xl border border-slate-100 shadow-sm hover:shadow-md hover:border-slate-200 transition-all duration-200 overflow-hidden text-left"
    >
      {/* Color strip */}
      <div className="flex h-20 w-full overflow-hidden">
        {colors.map((hex, i) => (
          <div
            key={i}
            className="flex-1 transition-transform duration-200 group-hover:scale-y-105"
            style={{ backgroundColor: hex }}
          />
        ))}
        {colors.length === 0 && (
          <div className="flex-1 bg-slate-100" />
        )}
      </div>

      {/* Info */}
      <div className="px-3 py-2">
        <p className="text-sm font-semibold text-slate-800 truncate leading-tight">
          {palette.name}
        </p>
        {moodNames.length > 0 && (
          <div className="mt-1">
            <MoodChips moodNames={moodNames} />
          </div>
        )}
        <div className="flex items-center justify-between gap-2 mt-1">
          <p className="text-[11px] text-slate-400 truncate min-w-0">
            by {palette.authorName?.startsWith('@')
              ? <Link to={`/profile/${palette.authorName.slice(1)}`} onClick={e => e.stopPropagation()} className="text-sky-500 hover:text-sky-600 hover:underline transition-colors">{palette.authorName}</Link>
              : (palette.authorName || 'Unknown')}
          </p>
          <div className="flex items-center gap-1 text-xs text-slate-400 flex-shrink-0">
            <Heart size={11} className={palette.hideLikes ? "text-slate-300" : "fill-red-400 text-red-400"} />
            {!palette.hideLikes && <span>{displayHeartCount(palette)}</span>}
          </div>
        </div>
      </div>
    </button>
  );
}

// ─── Gallery Detail Modal ─────────────────────────────────────────────────────

function GalleryDetailModal({ palette, onClose, user, navigate }) {
  const { ohuhuMode } = useAppContext();
  const [hearted, setHearted] = useState(false);
  const [heartCount, setHeartCount] = useState(palette?.heartCount || 0);
  const [heartLoading, setHeartLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [saveModalOpen, setSaveModalOpen] = useState(false);

  // Decode markers from palette code
  useEffect(() => {
    if (!palette?.code) return;
    try {
      const decoded = decodePaletteFromShare(palette.code);
      setMarkers(decoded || []);
    } catch {
      setMarkers([]);
    }
  }, [palette]);

  // Check if user has hearted this palette + reconcile delta
  useEffect(() => {
    if (!palette?.id) return;
    const base = palette.heartCount || 0;
    const delta = reconcileHeartBase(palette.id, base);
    setHeartCount(Math.max(0, base + delta));
    if (user) checkIfHearted(user.uid, palette.id).then(setHearted);
  }, [user, palette]);

  // Escape key to close
  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  // Body scroll lock
  useEffect(() => {
    document.documentElement.style.overflow = 'hidden';
    return () => { document.documentElement.style.overflow = ''; };
  }, []);

  const handleHeartToggle = async () => {
    if (!user) {
      navigate('/palettes');
      onClose();
      return;
    }
    if (heartLoading) return;
    setHeartLoading(true);

    const wasHearted = hearted;
    setHearted(!wasHearted);
    setHeartCount(c => wasHearted ? c - 1 : c + 1);

    const result = await heartPalette(user.uid, palette.id);
    if (!result.success) {
      setHearted(wasHearted);
      setHeartCount(c => wasHearted ? c + 1 : c - 1);
    } else {
      const base = palette.heartCount || 0;
      const prevDelta = getHeartDelta(palette.id);
      // hearting:   if snapshot doesn't have it yet (+1), if it does (delta was -1, now 0)
      // un-hearting: if snapshot has it (-1), if it didn't have it yet (cancel the +1, so 0)
      const newDelta = wasHearted
        ? (prevDelta > 0 ? 0 : -1)   // un-heart: was pending? cancel it. already synced? mark -1
        : (prevDelta < 0 ? 0 : 1);    // heart:    was pending removal? cancel it. not synced? mark +1
      setHeartDelta(palette.id, newDelta);
      setHeartCount(Math.max(0, base + newDelta));
    }
    setHeartLoading(false);
  };

  const handleCopyCode = () => {
    if (!palette?.code) return;
    navigator.clipboard.writeText(palette.code).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Resolve marker code taking ohuhuMode into account
  const getMarkerCode = (marker) => getMarkerCodeUtil(marker, ohuhuMode);

  if (!palette) return null;

  const allColors = markers.length > 0
    ? markers.map(m => m.hex)
    : (palette.previewColors || []);

  return createPortal(
    <div
      className="fixed inset-0 z-[60] backdrop-blur-sm bg-black/30 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget && !saveModalOpen) onClose(); }}
    >
      <div className="bg-white rounded-3xl max-w-lg w-full shadow-2xl border border-slate-100 flex flex-col max-h-[90vh] animate-slideUp">

        {/* Top color strip */}
        <div className="flex h-28 rounded-t-3xl overflow-hidden flex-shrink-0">
          {allColors.length > 0
            ? allColors.map((hex, i) => (
                <div key={i} className="flex-1" style={{ backgroundColor: hex }} />
              ))
            : <div className="flex-1 bg-slate-100" />
          }
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Header row */}
          <div className="flex items-start justify-between gap-3 mb-4">
            <div className="flex-1 min-w-0">
              <h2 className="text-lg font-bold text-slate-900 leading-tight truncate">
                {palette.name}
              </h2>
              <p className="text-sm text-slate-500 mt-0.5 truncate">
                by {palette.authorName?.startsWith('@')
                  ? <Link to={`/profile/${palette.authorName.slice(1)}`} onClick={onClose} className="text-sky-500 hover:text-sky-600 hover:underline transition-colors">{palette.authorName}</Link>
                  : (palette.authorName || 'Unknown')}
                {palette.createdAt && <> &middot; {formatPaletteDate(palette.createdAt)}</>}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-xl hover:bg-slate-100 transition-colors flex-shrink-0"
            >
              <X size={18} className="text-slate-500" />
            </button>
          </div>

          {/* Swatch grid — code inside square, brand below */}
          {markers.length > 0 && (
            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mb-4">
              {markers.map((m, i) => {
                const hex = m.hex || '#E5E7EB';
                const cleanHex = hex.replace('#', '');
                const r = parseInt(cleanHex.substr(0, 2), 16);
                const g = parseInt(cleanHex.substr(2, 2), 16);
                const b = parseInt(cleanHex.substr(4, 2), 16);
                const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
                const textColor = luminance > 0.5 ? '#1e293b' : '#ffffff';
                const rawCode = getMarkerCode(m);
                const codeSegments = rawCode.split('/').map(s => s.trim()).filter(Boolean);
                return (
                  <div key={i} className="flex flex-col">
                    <div
                      className="w-full aspect-square rounded-xl border-4 border-white shadow-inner relative"
                      style={{ backgroundColor: hex }}
                    >
                      <div className="absolute bottom-2 left-2 flex flex-wrap items-center gap-1">
                        {codeSegments.map((seg, idx) => (
                          <span
                            key={idx}
                            className="px-0.5 text-[10px] font-mono font-semibold uppercase tracking-[0.3em]"
                            style={{ color: textColor }}
                          >
                            {seg}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-[8px] font-semibold uppercase text-slate-500 truncate mt-1.5 px-2">
                      {m.brand}
                    </p>
                  </div>
                );
              })}
            </div>
          )}

          {/* Code box */}
          {palette.code && (
            <div className="bg-slate-50 rounded-xl p-3 flex items-center gap-2">
              <p className="text-xs font-mono text-slate-600 flex-1 break-all leading-relaxed">
                {palette.code}
              </p>
              <button
                onClick={handleCopyCode}
                className="p-2 rounded-lg hover:bg-slate-200 transition-colors flex-shrink-0"
                title="Copy code"
              >
                {copied
                  ? <Check size={15} className="text-green-600" />
                  : <Copy size={15} className="text-slate-500" />
                }
              </button>
            </div>
          )}

          {/* Moods */}
          {palette.moods && (
            <div className="mt-3">
              <MoodChips moodNames={parseMoods(palette.moods)} />
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center gap-3 p-5 pt-0 flex-shrink-0">
          {/* Heart */}
          <button
            onClick={handleHeartToggle}
            disabled={heartLoading}
            className={`flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium transition-all border ${
              hearted
                ? 'bg-red-50 border-red-200 text-red-600'
                : 'bg-slate-50 border-slate-200 text-slate-600 hover:bg-red-50 hover:border-red-200 hover:text-red-600'
            }`}
            title={user ? (hearted ? 'Remove heart' : 'Heart this palette') : 'Sign in to heart'}
          >
            {user
              ? <Heart size={15} className={hearted ? 'fill-red-500 text-red-500' : ''} />
              : <LogIn size={15} />
            }
            {(!palette.hideLikes || !user) && <span>{user ? heartCount : 'Sign in'}</span>}
          </button>

          {/* Save */}
          <button
            onClick={() => {
              if (!user) { navigate('/palettes'); onClose(); return; }
              setSaveModalOpen(true);
            }}
            disabled={markers.length === 0}
            className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 hover:border-emerald-300 transition-all disabled:opacity-40"
          >
            <BookmarkPlus size={15} />
            <span>Save</span>
          </button>
        </div>
      </div>
      {saveModalOpen && (
        <SavePaletteModal
          palette={palette}
          markers={markers}
          user={user}
          onClose={() => setSaveModalOpen(false)}
          onSaved={() => { setSaveModalOpen(false); onClose(); }}
        />
      )}
    </div>,
    document.body
  );
}

// ─── Save Palette Modal ───────────────────────────────────────────────────────

function SavePaletteModal({ palette, markers, user, onClose, onSaved }) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Derive collection-converted markers (computed once)
  const collectionMarkers = convertMarkersToCollection(markers);
  const collectionSize = getUserCollection().length;
  const noCollection = collectionSize === 0;

  useEffect(() => {
    const handle = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handle);
    return () => document.removeEventListener('keydown', handle);
  }, [onClose]);

  const doSave = async (markersToSave) => {
    setSaving(true);
    setError(null);
    // Check palette slot capacity first
    const existing = await getUserPalettes(user.uid);
    if (existing.success && existing.palettes.length >= 40) {
      setError('You have reached the 40 palette limit. Delete some palettes first.');
      setSaving(false);
      return;
    }
    const result = await savePalette(user.uid, markersToSave, palette.name);
    if (result.success) {
      setSuccess(true);
      setTimeout(() => onSaved(), 1200);
    } else {
      setError(result.error || 'Failed to save palette.');
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[70] backdrop-blur-sm bg-black/40 flex items-center justify-center p-4 animate-fadeOnly"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-white rounded-2xl max-w-sm w-full shadow-2xl border border-slate-100 overflow-hidden animate-slideUp">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-5 pb-3">
          <h3 className="text-base font-bold text-slate-900">Save palette</h3>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 transition-colors">
            <X size={16} />
          </button>
        </div>

        {success ? (
          <div className="px-5 pb-5 pt-2 text-center">
            <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-3">
              <Check size={24} className="text-emerald-600" />
            </div>
            <p className="text-sm font-medium text-slate-800">Saved to your palettes!</p>
          </div>
        ) : (
          <div className="px-5 pb-5 space-y-3">
            {error && (
              <p className="text-xs text-red-600 bg-red-50 rounded-lg px-3 py-2">{error}</p>
            )}

            {/* Save as-is */}
            <button
              onClick={() => doSave(markers)}
              disabled={saving}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 hover:border-emerald-300 hover:bg-emerald-50 transition-all group text-left disabled:opacity-50"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-400 to-teal-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Layers size={20} className="text-white" />
              </div>
              <div>
                <p className="text-sm font-semibold text-slate-800 group-hover:text-emerald-700 transition-colors">
                  Save as-is
                </p>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  Save all {markers.length} original markers exactly as they appear in this palette.
                </p>
              </div>
            </button>

            {/* Convert & Save */}
            <button
              onClick={() => doSave(collectionMarkers)}
              disabled={saving || noCollection || collectionMarkers.length === 0}
              className="w-full flex items-center gap-4 p-4 rounded-xl border-2 border-slate-100 hover:border-purple-300 hover:bg-purple-50 transition-all group text-left disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-400 to-violet-500 flex items-center justify-center flex-shrink-0 group-hover:scale-110 transition-transform">
                <Shuffle size={20} className="text-white" />
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <p className="text-sm font-semibold text-slate-800 group-hover:text-purple-700 transition-colors">
                    Convert & Save
                  </p>
                  {!noCollection && (
                    <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">
                      {collectionMarkers.length} markers
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">
                  {noCollection
                    ? 'No collection found. Add markers to your collection first.'
                    : collectionMarkers.length === 0
                    ? 'No markers in your collection match this palette.'
                    : `Each marker is matched to the closest one you own. Duplicates are removed — you may get fewer than ${markers.length} markers.`
                  }
                </p>
              </div>
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Gallery Page ─────────────────────────────────────────────────────────────

// ─── Coming Soon screen ───────────────────────────────────────────────────────

function GalleryComingSoon() {
  const { t } = useI18n();
  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-display animate-pulse font-bold heading-display mb-3 bg-gradient-to-r from-purple-400 to-pink-500">
          {t('gallery.title')} <LayoutGrid className="inline-block w-8 h-8 pb-1 text-pink-500" />
        </h2>
        {/* <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('gallery.subtitle')}
        </p> */}
      </div>

      {/* Coming soon card */}
      <div className="flex flex-col items-center justify-center gap-3">
        {/* Decorative icons */}
        <div className="flex items-end gap-3">
          <Flower size={22} className="text-pink-300 animate-float" style={{ animationDelay: '0ms' }} />
          <Stars size={22} className="text-purple-200 animate-float" style={{ animationDelay: '500ms' }} />
          <Sparkles size={22} className="text-amber-200 animate-float" style={{ animationDelay: '1000ms' }} />
          <Flower size={22} className="text-rose-300 animate-float" style={{ animationDelay: '1500ms' }} />
          <Stars size={22} className="text-violet-200 animate-float" style={{ animationDelay: '2000ms' }} />
        </div>

        <div className="max-w-md w-full text-center space-y-3">
          <p className="text-2xl font-bold text-slate-700">Coming Soon</p>
          <p className="text-sm text-slate-500 leading-relaxed">
            The community gallery is on its way! Discover and save palettes shared by fellow MarkerSync artists — launching very soon.
          </p>
          <div className="flex justify-center gap-2 pt-1">
            <span className="w-2 h-2 rounded-full bg-pink-300 animate-bounce" style={{ animationDelay: '0ms' }} />
            <span className="w-2 h-2 rounded-full bg-purple-300 animate-bounce" style={{ animationDelay: '150ms' }} />
            <span className="w-2 h-2 rounded-full bg-violet-300 animate-bounce" style={{ animationDelay: '300ms' }} />
          </div>
        </div>

        {/* Preview image */}
        <div className="relative mt-4 max-w-xl w-full">
          {/* Glow backdrop */}
          <div className="absolute inset-0 bg-gradient-to-br from-purple-300/40 to-pink-300/40 rounded-3xl blur-2xl scale-105 pointer-events-none" />

          {/* Tilted frame */}
          <div className="relative" style={{ transform: 'rotate(-1deg)' }}>
            {/* Gradient border ring */}
            <div className="p-[3px] rounded-[23px] bg-gradient-to-br from-purple-400 via-pink-400 to-rose-400 shadow-2xl">
              <div className="rounded-[20px] overflow-hidden">
                <img
                  src="/images/gallery-preview.jpg"
                  alt="Gallery preview"
                  className="w-full object-cover"
                  style={{ maxHeight: '420px' }}
                />
              </div>
            </div>

            {/* "Sneak Peek" ribbon badge — top-left corner */}
            <div
              className="absolute -top-4 -left-3 bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[11px] font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center gap-1.5"
              style={{ transform: 'rotate(1deg)' }}
            >
              <Sparkles size={11} />
              Sneak Peek
            </div>

            {/* Subtle shine overlay */}
            <div className="absolute inset-0 rounded-2xl bg-gradient-to-b from-white/10 to-transparent pointer-events-none" />
          </div>
        </div>

        {/* Decorative icons bottom */}
        <div className="flex items-start gap-3 mt-4">
          <Sparkles size={22} className="text-pink-200 animate-float" style={{ animationDelay: '2000ms' }} />
          <Flower size={22} className="text-purple-300 animate-float" style={{ animationDelay: '1500ms' }} />
          <Stars size={22} className="text-rose-300 animate-float" style={{ animationDelay: '1000ms' }} />
          <Sparkles size={22} className="text-violet-300 animate-float" style={{ animationDelay: '500ms' }} />
          <Flower size={22} className="text-amber-300 animate-float" style={{ animationDelay: '0ms' }} />
        </div>
      </div>
    </div>
  );
}

// ─── Gallery Page ─────────────────────────────────────────────────────────────

export default function GalleryPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [tab, setTab] = useState('popular');
  const [palettes, setPalettes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPalette, setSelectedPalette] = useState(null);
  const [galleryLive, setGalleryLive] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [applyModalOpen, setApplyModalOpen] = useState(false);
  const [lastSyncedAt, setLastSyncedAt] = useState(null);
  const [isTrusted, setIsTrusted] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeMoodFilters, setActiveMoodFilters] = useState([]);
  const [moodFilterOpen, setMoodFilterOpen] = useState(false);

  const filteredPalettes = useMemo(() => {
    let result = palettes;
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(p =>
        p.name.toLowerCase().includes(q) ||
        (p.authorName || '').toLowerCase().includes(q)
      );
    }
    if (activeMoodFilters.length > 0) {
      result = result.filter(p => {
        const paletteMoods = parseMoods(p.moods);
        return activeMoodFilters.some(m => paletteMoods.includes(m));
      });
    }
    return result;
  }, [palettes, searchQuery, activeMoodFilters]);

  useEffect(() => {
    checkGalleryAccess(user?.uid).then(trusted => {
      setGalleryLive(trusted);
      setAccessChecked(true);
    });
    if (user?.uid) {
      isTrustedPublisher(user.uid).then(setIsTrusted);
    }
  }, [user]);

  const loadPalettes = useCallback(async (activeTab) => {
    if (!galleryLive) return;
    setIsLoading(true);
    setPalettes([]);
    try {
      const [result, syncedAt] = await Promise.all([
        activeTab === 'popular' ? fetchPopularPalettes() : fetchRecentPalettes(),
        fetchLastSyncedAt(),
      ]);
      if (result.success) setPalettes(result.palettes);
      if (syncedAt) setLastSyncedAt(syncedAt);
    } finally {
      setIsLoading(false);
    }
  }, [galleryLive]);

  useEffect(() => {
    if (accessChecked) loadPalettes(tab);
  }, [tab, loadPalettes, accessChecked]);

  if (!accessChecked) return null;
  if (!galleryLive) return <GalleryComingSoon />;

  return (
    <div className="w-full mx-auto sm:px-6 lg:px-8 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h2 className="text-3xl md:text-4xl font-display font-bold heading-display mb-3 animate-pulse bg-gradient-to-r from-purple-400 to-pink-500">
          {t('gallery.title')} <LayoutGrid className="inline-block w-8 h-8 pb-1 text-pink-500" />
        </h2>
        <p className="text-base md:text-md text-elegant max-w-2xl mx-auto">
          {t('gallery.subtitle')}
        </p>
        <PageHelpModal
          title={t('gallery.help.title')}
          tips={[
            { text: t('gallery.help.tip1') },
            { text: t('gallery.help.tip2') },
            { text: t('gallery.help.tip3') },
            { text: t('gallery.help.tip4') },
          ]}
          trigger={
            <p className="text-sm justify-center text-slate-400 hover:text-slate-600 transition-colors cursor-pointer animate-pulse mt-2 mx-auto inline-flex items-center gap-2">
              {t('common.needHelp')}
            </p>
          }
        />
      </div>

      {/* Search bar + mood toggle */}
      <div className="flex gap-2">
        <div className="relative flex-1">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none z-10" />
          <input
            type="text"
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            placeholder="Search palettes or artists…"
            className="w-full pl-9 pr-9 py-2 text-xs border border-slate-200 rounded-xl bg-white/80 backdrop-blur-sm focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 focus:border-transparent transition-all"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X size={14} />
            </button>
          )}
        </div>
        <button
          onClick={() => setMoodFilterOpen(v => !v)}
          className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border rounded-xl transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 ${
            moodFilterOpen || activeMoodFilters.length > 0
              ? 'bg-purple-50 border-purple-300 text-purple-700'
              : 'bg-white/80 backdrop-blur-sm border-slate-200 text-slate-600 hover:bg-slate-50'
          }`}
          title="Filter by mood"
        >
          <Tag size={14} />
          {activeMoodFilters.length > 0 && (
            <span className="text-xs font-semibold">{activeMoodFilters.length}</span>
          )}
        </button>
      </div>

      {/* Mood filter chips — collapsible */}
      {moodFilterOpen && (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-2 animate-fadeIn">
          <div className="flex flex-wrap gap-1.5">
            {MOODS.map(mood => {
              const active = activeMoodFilters.includes(mood);
              return (
                <button
                  key={mood}
                  onClick={() => setActiveMoodFilters(prev =>
                    active ? prev.filter(m => m !== mood) : [...prev, mood]
                  )}
                  className={`text-xs font-medium px-2 py-0.5 rounded-full border transition-all ${
                    active
                      ? (SELECTED_COLORS[mood] || 'bg-slate-700 text-white border-slate-700')
                      : (MOOD_COLORS[mood] || 'bg-slate-100 text-slate-500 border-slate-200') + ' opacity-50 hover:opacity-80'
                  }`}
                >
                  {mood}
                </button>
              );
            })}
          </div>
          {activeMoodFilters.length > 0 && (
            <button
              onClick={() => setActiveMoodFilters([])}
              className="text-xs text-slate-400 hover:text-slate-600 underline"
            >
              Clear all
            </button>
          )}
        </div>
      )}

      {/* Tab switcher */}
      <div className="flex gap-2 mb-6">
        <button
          onClick={() => setTab('popular')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 ${
            tab === 'popular'
              ? 'bg-slate-800 text-white border-slate-800'
              : 'bg-white/80 backdrop-blur-sm text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <TrendingUp size={13} />
          Popular
        </button>
        <button
          onClick={() => setTab('recent')}
          className={`flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-medium transition-all duration-200 border focus:outline-none focus:ring-2 focus:ring-rose-pastel-300 ${
            tab === 'recent'
              ? 'bg-slate-800 text-white border-slate-800'
              : 'bg-white/80 backdrop-blur-sm text-slate-600 border-slate-200 hover:bg-slate-50'
          }`}
        >
          <Clock size={13} />
          Recent
        </button>
      </div>

      {/* Content */}
      {isLoading ? (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3">
          {Array.from({ length: 10 }).map((_, i) => (
            <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden animate-pulse">
              <div className="h-20 bg-slate-100" />
              <div className="px-3 py-2 space-y-1.5">
                <div className="h-3 bg-slate-100 rounded w-3/4" />
                <div className="h-2.5 bg-slate-100 rounded w-1/2" />
              </div>
            </div>
          ))}
        </div>
      ) : filteredPalettes.length === 0 ? (
        <div className="text-center py-20 text-slate-400">
          <p className="text-base font-medium">
            {palettes.length === 0 ? 'No palettes yet' : 'No palettes match your search'}
          </p>
          <p className="text-sm mt-1">
            {palettes.length === 0 ? 'Check back soon!' : 'Try different filters'}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-3 items-start">
          {filteredPalettes.map((palette) => (
            <GalleryCard
              key={palette.id}
              palette={palette}
              onClick={setSelectedPalette}
            />
          ))}
        </div>
      )}

      {/* Detail modal */}
      {selectedPalette && (
        <GalleryDetailModal
          palette={selectedPalette}
          onClose={() => setSelectedPalette(null)}
          user={user}
          navigate={navigate}
        />
      )}

      {/* Last sync footer + apply button */}
      <div className="flex flex-col items-center gap-2 pb-2">
        {!isLoading && lastSyncedAt && (
          <p className="text-xs text-slate-300">
            {formatLastSync(lastSyncedAt)}
          </p>
        )}
        {!isTrusted && (
          <button
            onClick={() => setApplyModalOpen(true)}
            className="inline-flex items-center gap-1.5 text-xs text-slate-400 hover:text-purple-500 transition-colors"
          >
            <UserPlus size={13} />
            Become a Publisher
          </button>
        )}
      </div>

      <ApplyPublisherModal isOpen={applyModalOpen} onClose={() => setApplyModalOpen(false)} />
    </div>
  );
}
